const TITLE_SNIPPET_LENGTH = 80;

function firstNonEmptyString(values) {
    const match = values.find((value) => typeof value === 'string' && value.trim());
    return match || '';
}

export function snippet(text) {
    const trimmed = String(text || '').trim().replace(/\s+/g, ' ');
    if (!trimmed) return '';
    if (trimmed.length <= TITLE_SNIPPET_LENGTH) return trimmed;
    return `${trimmed.slice(0, TITLE_SNIPPET_LENGTH - 3)}...`;
}

export function messageText(item) {
    if (!item || typeof item !== 'object') return '';
    return firstNonEmptyString([item.content, item.body, item.text, item.prompt, item.answer]);
}

export function messageRole(item) {
    const role = String(item?.role || item?.sender || item?.sender_type || '').toLowerCase();
    if (role === 'user' || role === 'human') return 'user';
    if (role === 'assistant' || role === 'ai' || role === 'model') return 'assistant';
    if (typeof item?.answer === 'string' && item.answer.trim() && !(typeof item?.prompt === 'string' && item.prompt.trim())) {
        return 'assistant';
    }
    return 'user';
}

function humanizeToolName(name) {
    if (typeof name !== 'string') return '';
    const words = name.replace(/_/g, ' ').trim();
    if (!words) return '';
    if (words.toLowerCase().startsWith('search ')) {
        return `searched ${words.slice(7).toLowerCase()}`;
    }
    return words.toLowerCase();
}

export function toolSummaryFromTrace(trace) {
    const rounds = Array.isArray(trace?.rounds) ? trace.rounds : [];
    const names = [];
    rounds.forEach((round) => {
        const calls = round?.toolCalls || round?.tool_calls || [];
        calls.forEach((call) => {
            const label = humanizeToolName(call?.name);
            if (label && !names.includes(label)) names.push(label);
        });
    });
    if (!names.length) return '';
    const sentence = names.join(', ');
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

export function downloadsFromResponse(downloads) {
    if (!Array.isArray(downloads)) return [];
    return downloads.reduce((acc, item) => {
        if (!item || typeof item !== 'object') return acc;
        const url = item.url || item.href;
        const name = item.name || item.filename || item.file_name;
        if (!url || !name) return acc;
        acc.push({ url, name });
        return acc;
    }, []);
}

export function conversationIdFromPromptResponse(data) {
    return data?.ai_conversation_id || data?.conversation?.ai_conversation_id || null;
}

export function answerFromPromptResponse(data) {
    return firstNonEmptyString([data?.answer, data?.message]);
}

function conversationArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.conversations)) return data.conversations;
    if (Array.isArray(data?.items)) return data.items;
    return [];
}

function firstUserPrompt(item) {
    if (typeof item?.prompt === 'string' && item.prompt.trim()) return item.prompt;
    const messages = Array.isArray(item?.messages) ? item.messages : [];
    const userMessage = messages.find((message) => messageRole(message) === 'user');
    return userMessage ? messageText(userMessage) : '';
}

export function normalizeConversations(data) {
    return conversationArray(data).reduce((acc, item) => {
        if (!item || typeof item !== 'object') return acc;
        const id = item.ai_conversation_id || item.id;
        if (!id) return acc;
        const title = (typeof item.title === 'string' && item.title.trim())
            || snippet(firstUserPrompt(item))
            || 'New conversation';
        acc.push({
            ai_conversation_id: String(id),
            title,
            updated_at: item.updated_at || item.updated_date || item.created_at || item.created_date || null
        });
        return acc;
    }, []);
}

function messageArray(data) {
    if (Array.isArray(data?.messages)) return data.messages;
    if (Array.isArray(data?.conversation?.messages)) return data.conversation.messages;
    if (Array.isArray(data)) return data;
    return [];
}

export function normalizeMessages(data) {
    return messageArray(data).reduce((acc, item, index) => {
        if (!item || typeof item !== 'object') return acc;
        const text = messageText(item);
        const downloads = downloadsFromResponse(item.downloads);
        const toolSummary = (typeof item.toolSummary === 'string' && item.toolSummary)
            || toolSummaryFromTrace(item.trace)
            || '';
        if (!text && !downloads.length && !toolSummary) return acc;
        acc.push({
            id: String(item.id || item.message_id || item.ai_message_id || `msg-${index}`),
            role: messageRole(item),
            text,
            toolSummary,
            downloads,
            status: 'sent'
        });
        return acc;
    }, []);
}
